import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { MenuItem, MenuCategory, MenuState, MenuSearchParams } from '../../types/menu';

const initialState: MenuState = {
  categories: [],
  items: [],
  popularItems: [],
  searchResults: [],
  isLoading: false,
  error: null,
  searchQuery: '',
  selectedCategory: null,
};

// Async thunks
export const fetchMenuCategories = createAsyncThunk(
  'menu/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const company = process.env.REACT_APP_COMPANY || 'QASTCO';
      const response = await fetch(`${process.env.REACT_APP_API_URL}/menu/categories?company=${company}`);

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      return await response.json();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMenuItems = createAsyncThunk(
  'menu/fetchItems',
  async (params: MenuSearchParams = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();

      // Add company parameter
      const company = process.env.REACT_APP_COMPANY || 'QASTCO';
      queryParams.append('company', company);

      if (params.query) queryParams.append('query', params.query);
      if (params.category) queryParams.append('category', params.category);
      if (params.dietaryFilter) queryParams.append('dietary', params.dietaryFilter);
      if (params.spiceLevel) queryParams.append('spice', params.spiceLevel);
      if (params.priceRange) {
        queryParams.append('minPrice', params.priceRange.min.toString());
        queryParams.append('maxPrice', params.priceRange.max.toString());
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/menu/items?${queryParams}`);

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      return await response.json();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPopularItems = createAsyncThunk(
  'menu/fetchPopularItems',
  async (_, { rejectWithValue }) => {
    try {
      const company = process.env.REACT_APP_COMPANY || 'QASTCO';
      const response = await fetch(`${process.env.REACT_APP_API_URL}/menu/popular?company=${company}`);

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      return await response.json();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const searchMenuByVoice = createAsyncThunk(
  'menu/searchByVoice',
  async (voiceQuery: string, { rejectWithValue }) => {
    try {
      const company = process.env.REACT_APP_COMPANY || 'QASTCO';
      const response = await fetch(`${process.env.REACT_APP_API_URL}/menu/voice-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: voiceQuery, company }),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      return await response.json();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setSelectedCategory: (state, action: PayloadAction<string | null>) => {
      state.selectedCategory = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchQuery = '';
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchMenuCategories.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMenuCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categories = action.payload.data || [];
      })
      .addCase(fetchMenuCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch items
      .addCase(fetchMenuItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMenuItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload.data || [];
      })
      .addCase(fetchMenuItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch popular items
      .addCase(fetchPopularItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPopularItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.popularItems = action.payload.data || [];
      })
      .addCase(fetchPopularItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Voice search
      .addCase(searchMenuByVoice.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchMenuByVoice.fulfilled, (state, action) => {
        state.isLoading = false;
        state.searchResults = action.payload.data || [];
      })
      .addCase(searchMenuByVoice.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSearchQuery,
  setSelectedCategory,
  clearSearchResults,
  clearError,
} = menuSlice.actions;

export default menuSlice.reducer;